export type RingtoneId =
  | 'gentle-ascent'
  | 'steady-support'
  | 'sunshine-wave'
  | 'care-pulse'
  | 'zen-lounge'

const RINGTONE_OPTIONS: { id: RingtoneId; label: string; file: string }[] = [
  { id: 'gentle-ascent', label: 'The Gentle Ascent', file: '/sounds/ringtone-gentle-ascent.wav' },
  { id: 'steady-support', label: 'Steady Support', file: '/sounds/ringtone-steady-support.wav' },
  { id: 'sunshine-wave', label: 'The Sunshine Wave', file: '/sounds/ringtone-sunshine-wave.wav' },
  { id: 'care-pulse', label: 'Care Pulse', file: '/sounds/ringtone-care-pulse.wav' },
  { id: 'zen-lounge', label: 'The Zen Lounge', file: '/sounds/ringtone-zen-lounge.wav' },
]

export const DIAL_TONE_URL = '/sounds/dial-tone.wav'

/**
 * Distinct "connecting" tone played AFTER a call is answered, while two-way
 * media is being established (the held "Connecting…" phase) — clearly different
 * from the outgoing dial-tone/ringback so the moment the other side truly
 * connects is unmistakable. See scripts/generate-sounds.mjs (renderConnectingTone).
 */
export const CONNECTING_TONE_URL = '/sounds/connecting.wav'

const STORAGE_KEY = 'messenger-ringtone-preference'

/** Map legacy ringtone ids to the new set. */
const RINGTONE_MIGRATION: Record<string, RingtoneId> = {
  default: 'gentle-ascent',
  classic: 'steady-support',
  soft: 'zen-lounge',
  'ascend-pop': 'sunshine-wave',
  'soft-tap': 'steady-support',
  spark: 'care-pulse',
  milestone: 'gentle-ascent',
  whisper: 'zen-lounge',
}

export function getRingtonePreference(): string {
  if (typeof window === 'undefined') return 'gentle-ascent'
  const stored = localStorage.getItem(STORAGE_KEY)
  if (!stored) return 'gentle-ascent'
  const direct = RINGTONE_OPTIONS.find((o) => o.id === stored)
  if (direct) return direct.id
  if (RINGTONE_MIGRATION[stored]) return RINGTONE_MIGRATION[stored]
  // Org catalog slot ids (custom uploads / builtin-* seeds).
  return stored
}

export function setRingtonePreference(id: string): void {
  localStorage.setItem(STORAGE_KEY, id)
}

/** Strip `builtin-` seed prefix so legacy prefs match catalog slot ids. */
export function normalizeSoundOptionId(id: string): string {
  return id.startsWith('builtin-') ? id.slice('builtin-'.length) : id
}

/** True when a stored preference refers to the same option as a selectable id. */
export function soundPreferenceMatchesOption(preferenceId: string, optionId: string): boolean {
  if (preferenceId === optionId) return true
  return normalizeSoundOptionId(preferenceId) === normalizeSoundOptionId(optionId)
}

/**
 * Map a stored preference onto the id used by the current selectable list
 * (org catalog slot id, or builtin id when no catalog).
 */
export function resolveSelectableSoundId(
  preferenceId: string,
  options: Array<{ id: string }>,
): string | null {
  if (options.length === 0) return null
  const exact = options.find((o) => o.id === preferenceId)
  if (exact) return exact.id
  const matched = options.find((o) => soundPreferenceMatchesOption(preferenceId, o.id))
  return matched?.id ?? null
}

/** Absolute URL for audio assets (Capacitor WebView resolves relative paths inconsistently). */
export function resolveMediaUrl(path: string): string {
  if (typeof window === 'undefined') return path
  if (/^https?:\/\//i.test(path)) return path
  try {
    return new URL(path, window.location.href).href
  } catch {
    return path
  }
}

function getRingtoneUrl(id: string = getRingtonePreference()): string {
  return resolveMediaUrl(
    RINGTONE_OPTIONS.find((o) => o.id === id)?.file ?? RINGTONE_OPTIONS[0].file,
  )
}

/** Resolve a ringtone preference against an org catalog (slot id or builtin id). */
export function resolveRingtonePlaybackUrl(
  preferenceId: string,
  catalogSlots: Array<{ id: string; attachmentUrl: string }> | null | undefined,
): string {
  if (catalogSlots && catalogSlots.length > 0) {
    const slot =
      catalogSlots.find((s) => s.id === preferenceId) ??
      catalogSlots.find((s) => s.id === `builtin-${preferenceId}`) ??
      catalogSlots.find((s) => soundPreferenceMatchesOption(preferenceId, s.id))
    if (slot) return resolveMediaUrl(slot.attachmentUrl)
    // Preference not in the curated list — fall back to bundled builtins,
    // never silently play catalog[0] (that made every selection sound like default).
  }
  const normalized = normalizeSoundOptionId(preferenceId)
  const asBuiltin =
    RINGTONE_OPTIONS.find((o) => o.id === preferenceId) ??
    RINGTONE_OPTIONS.find((o) => o.id === normalized)
  if (asBuiltin) return resolveMediaUrl(asBuiltin.file)
  return getRingtoneUrl()
}

/** Options shown in Display settings — org catalog when present, else all builtins. */
export function listSelectableRingtones(
  catalogSlots: Array<{ id: string; label: string; attachmentUrl: string }> | null | undefined,
): Array<{ id: string; label: string; url: string }> {
  if (catalogSlots && catalogSlots.length > 0) {
    return catalogSlots.map((s) => ({
      id: s.id,
      label: s.label,
      url: resolveMediaUrl(s.attachmentUrl),
    }))
  }
  return RINGTONE_OPTIONS.map((o) => ({
    id: o.id,
    label: o.label,
    url: resolveMediaUrl(o.file),
  }))
}

// ---------------------------------------------------------------------------
// Message chime preference
// ---------------------------------------------------------------------------

export type ChimeId =
  | 'ascend-pop'
  | 'soft-tap'
  | 'little-spark'
  | 'milestone'
  | 'whisper-alert'

const CHIME_OPTIONS: { id: ChimeId; label: string; file: string }[] = [
  { id: 'ascend-pop', label: 'Ascend Pop', file: '/sounds/chime-ascend-pop.wav' },
  { id: 'soft-tap', label: 'Soft Tap', file: '/sounds/chime-soft-tap.wav' },
  { id: 'little-spark', label: 'Little Spark', file: '/sounds/chime-little-spark.wav' },
  { id: 'milestone', label: 'The Milestone', file: '/sounds/chime-milestone.wav' },
  { id: 'whisper-alert', label: 'Whisper Alert', file: '/sounds/chime-whisper-alert.wav' },
]

/**
 * Built-in chime used for a chat message that arrives DURING a call.
 *
 * A BUILT-IN id on purpose: those assets are the ones primed by
 * `unlockAudioOnGesture`, which is what makes the sound audible on iOS / the
 * Capacitor WKWebView mid-call. `soft-tap` is the shortest and least intrusive
 * of the catalogue, so it sits under live call audio.
 */
export const IN_CALL_MESSAGE_CHIME_ID: ChimeId = 'soft-tap'

const CHIME_STORAGE_KEY = 'messenger-chime-preference'

const CHIME_MIGRATION: Record<string, ChimeId> = {
  default: 'ascend-pop',
  spark: 'little-spark',
  whisper: 'whisper-alert',
}

export function getChimePreference(): string {
  if (typeof window === 'undefined') return 'ascend-pop'
  const stored = localStorage.getItem(CHIME_STORAGE_KEY)
  if (!stored) return 'ascend-pop'
  const direct = CHIME_OPTIONS.find((o) => o.id === stored)
  if (direct) return direct.id
  if (CHIME_MIGRATION[stored]) return CHIME_MIGRATION[stored]
  return stored
}

export function setChimePreference(id: string): void {
  localStorage.setItem(CHIME_STORAGE_KEY, id)
}

function getChimeUrl(id: string = getChimePreference()): string {
  const file = CHIME_OPTIONS.find((o) => o.id === id)?.file ?? CHIME_OPTIONS[0].file
  return resolveMediaUrl(file)
}

export function resolveChimePlaybackUrl(
  preferenceId: string,
  catalogSlots: Array<{ id: string; attachmentUrl: string }> | null | undefined,
): string {
  if (catalogSlots && catalogSlots.length > 0) {
    const slot =
      catalogSlots.find((s) => s.id === preferenceId) ??
      catalogSlots.find((s) => s.id === `builtin-${preferenceId}`) ??
      catalogSlots.find((s) => soundPreferenceMatchesOption(preferenceId, s.id))
    if (slot) return resolveMediaUrl(slot.attachmentUrl)
  }
  const normalized = normalizeSoundOptionId(preferenceId)
  const asBuiltin =
    CHIME_OPTIONS.find((o) => o.id === preferenceId) ??
    CHIME_OPTIONS.find((o) => o.id === normalized)
  if (asBuiltin) return resolveMediaUrl(asBuiltin.file)
  return getChimeUrl()
}

export function listSelectableChimes(
  catalogSlots: Array<{ id: string; label: string; attachmentUrl: string }> | null | undefined,
): Array<{ id: string; label: string; url: string }> {
  if (catalogSlots && catalogSlots.length > 0) {
    return catalogSlots.map((s) => ({
      id: s.id,
      label: s.label,
      url: resolveMediaUrl(s.attachmentUrl),
    }))
  }
  return CHIME_OPTIONS.map((o) => ({
    id: o.id,
    label: o.label,
    url: resolveMediaUrl(o.file),
  }))
}